import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getReaderFollowGraph } from '../../services/followService';
import TbrButton from '../ui/TbrButton';
import { genreColor } from '../../utils/genreColor';
import './ReaderDiscovery.css';

const catalogUrl = 'https://bookish-master-data.s3.us-west-2.amazonaws.com/market.json';
const normalized = value => String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
function safeUrl(value) {
  if (typeof value !== 'string' || !value.trim()) return '';
  try { const url = new URL(value, 'https://bookishcircle.com'); return url.protocol === 'https:' ? url.href : ''; } catch { return ''; }
}
export default function ReaderDiscovery() {
  const [state, setState] = useState({ loading: true, books: [], genres: [] });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setState({ loading: true, books: [], genres: [] });
    Promise.allSettled([
      fetch(catalogUrl, { signal: controller.signal, cache: 'no-store' }).then(async response => {
        if (!response.ok) throw new Error('Catalog unavailable');
        const data = await response.json();
        if (!Array.isArray(data.books)) throw new Error('Invalid catalog');
        return data.books;
      }),
      getReaderFollowGraph({ signal: controller.signal }),
    ]).then(([catalog, graph]) => {
      if (controller.signal.aborted) return;
      const genres = graph.status === 'fulfilled' ? graph.value.genres : [];
      const books = catalog.status === 'fulfilled' ? catalog.value.filter(book => book.title && safeUrl(book.productUrl)) : [];
      const matches = books.filter(book => (book.genres || []).some(genre => genres.some(follow => normalized(follow.name) === normalized(genre))));
      setState({ loading: false, genres, books: (matches.length ? matches : books).slice(0, 3), matched: matches.length > 0, error: catalog.status === 'rejected', circlesError: graph.status === 'rejected' });
    });
    return () => controller.abort();
  }, [attempt]);
  return <section className="reader-discovery" aria-labelledby="discovery-heading">
    <p className="reader-discovery-eyebrow">Make yourself at home</p>
    <h2 id="discovery-heading">Your next chapter starts here</h2>
    <p>A familiar favorite, a new voice, a book you can’t put down. There’s room for all of them here.</p>
    <nav className="reader-discovery-paths" aria-label="Explore your Bookish Circle">
      <Link to="?tab=mycircles"><strong>Your circles</strong><span>Follow the stories and authors you love</span></Link>
      <Link to="?tab=mytbr"><strong>Your TBR</strong><span>A little shelf of possibilities</span></Link>
      <Link to="/lady-liza/spotlight"><strong>Lady Liza’s pick</strong><span>Meet this month’s featured book</span></Link>
    </nav>
    {state.genres.length > 0 && <div className="reader-discovery-genres" aria-label="Explore your genres">{state.genres.map(genre => <Link key={genre.id} to={`/market?genre=${encodeURIComponent(genre.name)}`} style={{ borderColor: genreColor(genre.name) }}>{genre.name}</Link>)}</div>}
    <h3>{state.matched ? 'From the genres you love' : 'A few books to wander into'}</h3>
    <p className="reader-discovery-hint">Open a cover to take a closer look. Tap TBR to save a book for later.</p>
    {state.loading ? <p role="status">Finding a few good reads…</p> : state.error ? <p>Our book shelf couldn’t load. <button onClick={() => setAttempt(value => value + 1)}>Try again</button></p> : <>
      {state.circlesError && <p>Showing books from the whole market while your circles are unavailable. <button onClick={() => setAttempt(value => value + 1)}>Retry your circles</button></p>}
      <div className="reader-discovery-books">{state.books.map(book => {
        const url = safeUrl(book.productUrl);
        const cover = safeUrl(book.coverImageUrl || book.formats?.find(format => format.coverImageUrl)?.coverImageUrl);
        const authorUrl = book.subdomain ? safeUrl(`https://${book.subdomain.replace(/^https?:\/\//, '').split('/')[0]}`) : new URL(url).origin;
        return <article key={url}>
          <a href={url} className="reader-discovery-cover" aria-label={`View ${book.title}`}>{cover ? <img src={cover} alt={book.title} loading="lazy" /> : <span>{book.title}</span>}</a>
          <h4><a href={url}>{book.title}</a></h4>
          {book.authorName && <a className="reader-discovery-author" href={authorUrl}>{book.authorName}</a>}
          <TbrButton book={book} productUrl={url} coverImageUrl={cover} />
        </article>;
      })}</div>
      {!state.books.length && <p>Your next favorite may be just around the corner. Explore the market or visit your circles.</p>}
    </>}
    <Link className="reader-discovery-market" to="/market">Wander through the book market →</Link>
  </section>;
}
